// src/pages/DashboardPage.js
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  ArcElement, PointElement, LineElement, Tooltip, Legend, Filler
} from 'chart.js';
import { dashboard } from '../utils/api';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, Tooltip, Legend, Filler);

const CHART_DEFAULTS = {
  plugins: { legend: { labels: { color: '#94a3b8', font: { size: 11 } } } },
  scales: {
    x: { ticks: { color: '#64748b' }, grid: { color: '#1e2d45' } },
    y: { ticks: { color: '#64748b' }, grid: { color: '#1e2d45' } }
  }
};

const COLORS = ['#00d4ff','#7c83fd','#00e676','#ffab40','#ff4d6d','#f9a8d4','#86efac','#fcd34d'];

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="stat-card" style={{ '--accent': accent }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: accent }}>{value ?? '—'}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="card">
      <div className="card-header"><span className="card-title">{title}</span></div>
      {children}
    </div>
  );
}

export default function DashboardPage() {
  const { data, isLoading } = useQuery({ queryKey: ['dashStats'], queryFn: () => dashboard.stats().then(r => r.data.data) });
  const { data: suspData } = useQuery({ queryKey: ['suspicious'], queryFn: () => dashboard.suspicious().then(r => r.data.data) });
  const navigate = useNavigate();

  if (isLoading) return <div className="loading-page"><span className="spinner" style={{ width: 36, height: 36 }} /></div>;
  if (!data) return null;

  const { totals, charts, recentActivity } = data;

  const makeBar = (items, label) => ({
    labels: items.map(i => i.label || i.name || '—'),
    datasets: [{ label, data: items.map(i => i.value || i.count || 0),
      backgroundColor: COLORS, borderRadius: 4 }]
  });

  const makePie = (items) => ({
    labels: items.map(i => i.label || i.gender || '—'),
    datasets: [{ data: items.map(i => i.value || i.count || 0),
      backgroundColor: COLORS, borderWidth: 0 }]
  });

  const trendData = {
    labels: charts.registrationTrend.map(r => r.month),
    datasets: [{
      label: 'Students Registered',
      data: charts.registrationTrend.map(r => r.students),
      borderColor: '#00d4ff',
      backgroundColor: 'rgba(0,212,255,0.08)',
      fill: true,
      tension: 0.4,
      pointBackgroundColor: '#00d4ff'
    }]
  };

  const noAxes = { plugins: { legend: { labels: { color: '#94a3b8', font: { size: 11 } } } } };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* KPI Row */}
      <div className="stat-grid" style={{ marginBottom: 24 }}>
        <StatCard label="Total Students"       value={totals.students}        accent="#00d4ff" sub="Active enrolments" />
        <StatCard label="Total Devices"         value={totals.devices}         accent="#7c83fd" sub="Registered MACs" />
        <StatCard label="Enabled Devices"       value={totals.enabledDevices}  accent="#00e676" sub="Allowed on network" />
        <StatCard label="Disabled Devices"      value={totals.disabledDevices} accent="#ff4d6d" sub="Blocked from network" />
      </div>

      {/* Registration trend */}
      <div style={{ marginBottom: 20 }}>
        <ChartCard title="Student Registration Trend (Last 12 Months)">
          <Line data={trendData} options={{ ...CHART_DEFAULTS, responsive: true }} height={90} />
        </ChartCard>
      </div>

      {/* Row 2 */}
      <div className="chart-grid" style={{ marginBottom: 20 }}>
        <ChartCard title="Students by Course">
          <Bar data={makeBar(charts.courseStats, 'Students')} options={{ ...CHART_DEFAULTS, indexAxis: 'y', responsive: true }} />
        </ChartCard>
        <ChartCard title="Students by School">
          <Bar data={makeBar(charts.institutionStats, 'Students')} options={{ ...CHART_DEFAULTS, indexAxis: 'y', responsive: true }} />
        </ChartCard>
      </div>

      {/* Row 3 */}
      <div className="chart-grid" style={{ marginBottom: 20 }}>
        <ChartCard title="Gender Distribution">
          <div style={{ maxWidth: 260, margin: '0 auto' }}>
            <Pie data={makePie(charts.genderStats)} options={noAxes} />
          </div>
        </ChartCard>
        <ChartCard title="Device Types">
          <div style={{ maxWidth: 260, margin: '0 auto' }}>
            <Doughnut data={makePie(charts.deviceTypeStats)} options={noAxes} />
          </div>
        </ChartCard>
      </div>

      {/* Row 4 */}
      <div className="chart-grid" style={{ marginBottom: 20 }}>
        <ChartCard title="Students by Academic Year">
          <Bar data={{
            labels: charts.yearStats.map(r => r.label),
            datasets: [{ label: 'Students', data: charts.yearStats.map(r => r.value),
              backgroundColor: '#00d4ff', borderRadius: 4 }]
          }} options={{ ...CHART_DEFAULTS, responsive: true }} />
        </ChartCard>
        <ChartCard title="Top Device Vendors">
          <Bar data={makeBar(charts.vendorStats, 'Devices')} options={{ ...CHART_DEFAULTS, indexAxis: 'y', responsive: true }} />
        </ChartCard>
      </div>

      {/* Row 5 */}
      <div className="chart-grid" style={{ marginBottom: 20 }}>
        <ChartCard title="Students by Nationality (Top 15)">
          <Bar data={makeBar(charts.countryStats, 'Students')} options={{ ...CHART_DEFAULTS, responsive: true }} />
        </ChartCard>
        <ChartCard title="Devices per Student">
          <Bar data={{
            labels: charts.devicesPerStudent.map(r => `${r.label} device${r.label !== '1' ? 's' : ''}`),
            datasets: [{ label: 'Students', data: charts.devicesPerStudent.map(r => r.students),
              backgroundColor: '#7c83fd', borderRadius: 4 }]
          }} options={{ ...CHART_DEFAULTS, responsive: true }} />
        </ChartCard>
      </div>

      {/* Suspicious activity alert */}
      {suspData?.manyDevices?.length > 0 && (
        <div className="alert alert-danger" style={{ marginBottom: 20 }}>
          ⚠️ <strong>{suspData.manyDevices.length} student(s)</strong> have more than 5 registered devices — possible abuse.{' '}
          <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/ml')}>View in ML Insights →</span>
        </div>
      )}

      {/* Recent activity */}
      <ChartCard title="Recent Activity">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Action</th><th>Entity</th><th>Admin</th><th>Time</th>
              </tr>
            </thead>
            <tbody>
              {recentActivity.map((a, i) => (
                <tr key={i}>
                  <td><code style={{ fontSize: '0.75rem', color: 'var(--primary)' }}>{a.action}</code></td>
                  <td>{a.entity_type}</td>
                  <td>{a.admin_name || '—'}</td>
                  <td className="text-mono">{new Date(a.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>
    </div>
  );
}
